// 2026-10-21 오너 — "항목 사이사이에 좌우 크기 조절할 수 있게".
//
// 콘솔의 표는 공용 컴포넌트 없이 화면마다 <table> 을 직접 그린다(40여 곳). 화면을 하나씩 고치지 않고
// 앱 틀(AppShell)의 본문(main) 한 곳에서 처리한다:
//   · 머리글 칸(th)의 오른쪽 끝 6px 를 잡고 끌면 그 열의 폭이 바뀐다. 처음 끄는 순간 표를 지금 보이는
//     폭 그대로 고정 배치(table-layout: fixed)로 바꾼다 — 그래야 줄이는 쪽으로도 움직인다.
//   · 그 끝을 두 번 누르면 원래(자동) 폭으로 돌아간다.
//   · 폭은 이 브라우저에만 화면(주소) · 머리글 이름 묶음별로 적어 두고, 같은 표가 다시 그려지면 다시 건다.
// 머리글이 두 줄 이상이거나 합친 칸(colSpan)이 있는 표는 건드리지 않는다(고정 배치가 첫 줄로 폭을 잡는다).

const EDGE_PX = 6;
const MIN_PX = 40;
const STORE_KEY = 'bhops.table-col-widths.v1';

type Store = Record<string, number[]>;

function readStore(): Store {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    return raw ? (JSON.parse(raw) as Store) : {};
  } catch {
    return {};
  }
}

function writeStore(store: Store): void {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    // 저장소를 못 쓰면(사생활 창 등) 이번 화면에서만 유지된다.
  }
}

/** 크기를 조절할 수 있는 표의 머리글 칸들 — 한 줄짜리 머리글만. */
function headerCells(table: HTMLTableElement): HTMLTableCellElement[] | null {
  const head = table.tHead;
  if (!head || head.rows.length !== 1) return null;
  const cells = Array.from(head.rows[0]!.cells);
  if (cells.length < 2 || cells.some((c) => c.colSpan > 1)) return null;
  return cells;
}

function storeKey(table: HTMLTableElement, cells: HTMLTableCellElement[]): string {
  const sig = cells.map((c) => (c.textContent ?? '').trim()).join('|');
  return `${window.location.pathname}::${sig}`;
}

function applyWidths(table: HTMLTableElement, cells: HTMLTableCellElement[], widths: number[]) {
  cells.forEach((c, i) => {
    const w = `${Math.round(widths[i]!)}px`;
    c.style.width = w;
    c.style.minWidth = w;
    c.style.maxWidth = w;
  });
  const total = `${Math.round(widths.reduce((s, w) => s + w, 0))}px`;
  table.style.tableLayout = 'fixed';
  table.style.width = total;
  table.style.minWidth = total;
  table.dataset.colResized = '1';
}

function clearWidths(table: HTMLTableElement, cells: HTMLTableCellElement[]) {
  // 구글 시트 거울 표(2026-10-23)는 '원래 폭'이 시트 열 너비다 — 머리글 칸의 data-sheet-width 로 되돌린다.
  if (table.dataset.sheetTable !== undefined) {
    applyWidths(
      table,
      cells,
      cells.map((c) => Number(c.dataset.sheetWidth) || c.getBoundingClientRect().width),
    );
    delete table.dataset.colResized;
    return;
  }
  cells.forEach((c) => {
    c.style.width = '';
    c.style.minWidth = '';
    c.style.maxWidth = '';
  });
  table.style.tableLayout = '';
  table.style.width = '';
  table.style.minWidth = '';
  delete table.dataset.colResized;
}

/**
 * 감싼 상자가 머리글 옆 세로 스크롤바 자리를 머리글 색으로 칠할 높이(globals.css --kuun-thead-h).
 * 표가 상자 위 · 왼쪽에 딱 붙어 있고 세로 스크롤바가 있을 때만 — 여백(padding) 있는 카드에서는 칠한 띠가
 * 머리글과 어긋나 표 위에 색 띠가 떴다(2026-10-22 오너 "표가 너무 이상하지 않니").
 */
function paintScrollbarBesideHead(table: HTMLTableElement) {
  const parent = table.parentElement;
  if (!parent) return;
  const head = table.tHead;
  const p = parent.getBoundingClientRect();
  const t = table.getBoundingClientRect();
  const flush =
    Math.abs(t.top - (p.top + parent.clientTop)) <= 1 &&
    Math.abs(t.left - (p.left + parent.clientLeft)) <= 1;
  const hasVScroll = parent.offsetWidth - parent.clientWidth - parent.clientLeft * 2 > 0;
  if (head && flush && hasVScroll)
    parent.style.setProperty('--kuun-thead-h', `${head.offsetHeight}px`);
  else parent.style.removeProperty('--kuun-thead-h');
}

/** 포인터가 머리글 칸의 오른쪽 끝(마지막 칸 제외)에 있으면 그 칸. */
function edgeCell(root: HTMLElement, e: MouseEvent): HTMLTableCellElement | null {
  const target = e.target as Element | null;
  const th = target?.closest?.('th') as HTMLTableCellElement | null;
  if (!th || !root.contains(th) || !th.closest('thead')) return null;
  const table = th.closest('table') as HTMLTableElement | null;
  const cells = table ? headerCells(table) : null;
  if (!cells || cells.indexOf(th) === cells.length - 1) return null;
  // 이름 없는 칸(색 띠 · 선택 상자)은 폭을 바꿀 자리가 아니다 — 구분선도 없다(globals.css).
  if (!th.hasChildNodes()) return null;
  const r = th.getBoundingClientRect();
  return e.clientX >= r.right - EDGE_PX && e.clientX <= r.right + 1 ? th : null;
}

export function installColumnResize(root: HTMLElement): () => void {
  let drag: {
    table: HTMLTableElement;
    cells: HTMLTableCellElement[];
    index: number;
    startX: number;
    widths: number[];
  } | null = null;

  const onMove = (e: PointerEvent) => {
    if (drag) {
      const next = drag.widths.slice();
      next[drag.index] = Math.max(MIN_PX, drag.widths[drag.index]! + (e.clientX - drag.startX));
      applyWidths(drag.table, drag.cells, next);
      return;
    }
    root.classList.toggle('kuun-col-resize-hover', !!edgeCell(root, e));
  };

  const suppressClick = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const onUp = () => {
    if (!drag) return;
    const { table, cells } = drag;
    const widths = cells.map((c) => c.getBoundingClientRect().width);
    const store = readStore();
    store[storeKey(table, cells)] = widths.map((w) => Math.round(w));
    writeStore(store);
    drag = null;
    document.body.classList.remove('kuun-col-resizing');
    // 끌기를 마친 뒤 이어지는 클릭이 머리글 정렬을 건드리지 않게 한 번 삼킨다.
    window.addEventListener('click', suppressClick, { capture: true, once: true });
    window.setTimeout(() => window.removeEventListener('click', suppressClick, true), 0);
  };

  const onDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    const th = edgeCell(root, e);
    if (!th) return;
    const table = th.closest('table') as HTMLTableElement;
    const cells = headerCells(table)!;
    e.preventDefault();
    e.stopPropagation();
    const widths = cells.map((c) => c.getBoundingClientRect().width);
    applyWidths(table, cells, widths);
    drag = { table, cells, index: cells.indexOf(th), startX: e.clientX, widths };
    document.body.classList.add('kuun-col-resizing');
  };

  const onDblClick = (e: MouseEvent) => {
    const th = edgeCell(root, e);
    if (!th) return;
    const table = th.closest('table') as HTMLTableElement;
    const cells = headerCells(table)!;
    const store = readStore();
    delete store[storeKey(table, cells)];
    writeStore(store);
    clearWidths(table, cells);
    delete table.dataset.colApplied;
  };

  // 표가 (다시) 그려지면 적어 둔 폭을 건다.
  let frame = 0;
  const restore = () => {
    frame = 0;
    const store = readStore();
    root.querySelectorAll('table').forEach((table) => {
      paintScrollbarBesideHead(table);
      const cells = headerCells(table);
      if (!cells) return;
      const key = storeKey(table, cells);
      if (table.dataset.colApplied === key) return;
      table.dataset.colApplied = key;
      const widths = store[key];
      if (widths && widths.length === cells.length) applyWidths(table, cells, widths);
    });
  };
  const observer = new MutationObserver(() => {
    if (!frame && !drag) frame = window.requestAnimationFrame(restore);
  });
  observer.observe(root, { childList: true, subtree: true });
  restore();

  // 마우스 끌기가 글자 선택 · 자동 스크롤로 번지지 않게 — 포인터 이벤트만 막으면 mousedown 은 그대로 간다.
  const onMouseDown = (e: MouseEvent) => {
    if (drag || edgeCell(root, e)) e.preventDefault();
  };
  root.addEventListener('pointerdown', onDown, true);
  root.addEventListener('mousedown', onMouseDown, true);
  root.addEventListener('dblclick', onDblClick, true);
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  return () => {
    observer.disconnect();
    if (frame) window.cancelAnimationFrame(frame);
    root.removeEventListener('pointerdown', onDown, true);
    root.removeEventListener('mousedown', onMouseDown, true);
    root.removeEventListener('dblclick', onDblClick, true);
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    root.classList.remove('kuun-col-resize-hover');
  };
}
